import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import CepInput from '../../../components/shared/CepInput';
import { maskPhone, maskCPFOrCNPJ } from '../../../utils/masks';
import { validateCPF, validateCNPJ } from '../../../utils/validators';
import { ArrowLeft, Save, Check, X } from 'lucide-react';

const TIPOS_ENTREGA = ['Motoboy', 'Transportadora', 'Terceirizado', 'Retirada'];
const TURNOS = ['Manhã', 'Tarde'];

export default function ClienteForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isNew = !id;

  const [form, setForm] = useState({
    nome_contato: '', nome_empresa: '', cpf_cnpj: '', email: '', senha: '',
    whatsapp: '', cep: '', logradouro: '', numero: '', bairro: '', cidade: '', uf: '',
    ponto_referencia: '', tipo_cliente_id: '', rota_id: '', turno: 'Manhã',
    tipo_entrega: 'Motoboy', prazo_24h: false, status: 'Ativo',
    endereco_entrega_diferente: false,
    endereco_entrega: { cep: '', logradouro: '', numero: '', bairro: '', cidade: '', uf: '', ponto_referencia: '' },
  });
  const [tiposCliente, setTiposCliente] = useState([]);
  const [rotas, setRotas] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [cpfCnpjError, setCpfCnpjError] = useState('');
  const [aprovando, setAprovando] = useState(false);
  const [isPendente, setIsPendente] = useState(false);
  const [motivoRejeicao, setMotivoRejeicao] = useState('');

  useEffect(() => {
    const promises = [
      isAdmin ? api.get('/configuracoes/tipos-cliente') : Promise.resolve({ data: [] }),
      api.get('/rotas?is_ativo=true'),
    ];
    if (!isNew) promises.push(api.get(`/clientes/${id}`));
    Promise.all(promises).then(([tipos, rots, cliente]) => {
      setTiposCliente(tipos.data);
      setRotas(rots.data);
      if (cliente) {
        const c = cliente.data;
        setForm({ ...form, ...c });
        setIsPendente(c.status === 'Pendente');
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, [id]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setEntrega = (k, v) => setForm(f => ({ ...f, endereco_entrega: { ...f.endereco_entrega, [k]: v } }));
  const handleCepFound = (addr) => addr && setForm(f => ({ ...f, logradouro: addr.logradouro, bairro: addr.bairro, cidade: addr.cidade, uf: addr.uf }));
  const handleCepEntregaFound = (addr) => addr && setForm(f => ({ ...f, endereco_entrega: { ...f.endereco_entrega, logradouro: addr.logradouro, bairro: addr.bairro, cidade: addr.cidade, uf: addr.uf } }));

  const handleCpfCnpj = (raw) => {
    const masked = maskCPFOrCNPJ(raw);
    set('cpf_cnpj', masked);
    const digits = masked.replace(/\D/g, '');
    if (digits.length === 11) {
      setCpfCnpjError(validateCPF(masked) ? '' : 'CPF inválido');
    } else if (digits.length === 14) {
      setCpfCnpjError(validateCNPJ(masked) ? '' : 'CNPJ inválido');
    } else {
      setCpfCnpjError('');
    }
  };

  const handleSave = async () => {
    if (form.cpf_cnpj) {
      const digits = form.cpf_cnpj.replace(/\D/g, '');
      if (digits.length === 11 && !validateCPF(form.cpf_cnpj)) {
        setCpfCnpjError('CPF inválido — verifique o número');
        return;
      }
      if (digits.length === 14 && !validateCNPJ(form.cpf_cnpj)) {
        setCpfCnpjError('CNPJ inválido — verifique o número');
        return;
      }
    }
    setSaving(true);
    setError('');
    try {
      if (isNew) {
        await api.post('/clientes', form);
      } else {
        await api.put(`/clientes/${id}`, form);
      }
      navigate('/admin/clientes');
    } catch (err) {
      setError(err.userMessage || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handleAprovar = async () => {
    if (!form.tipo_cliente_id || !form.rota_id || !form.turno || !form.tipo_entrega) {
      setError('Preencha Tipo de Cliente, Rota, Turno e Tipo de Entrega para aprovar');
      return;
    }
    setAprovando(true);
    try {
      await api.put(`/clientes/${id}/aprovar`, {
        acao: 'aprovar',
        tipo_cliente_id: form.tipo_cliente_id,
        rota_id: form.rota_id,
        turno: form.turno,
        tipo_entrega: form.tipo_entrega,
        prazo_24h: form.prazo_24h,
      });
      navigate('/admin/clientes');
    } catch (err) {
      setError(err.userMessage || 'Erro ao aprovar');
    } finally {
      setAprovando(false);
    }
  };

  const handleRejeitar = async () => {
    setAprovando(true);
    try {
      await api.put(`/clientes/${id}/aprovar`, { acao: 'rejeitar', motivo_rejeicao: motivoRejeicao });
      navigate('/admin/clientes');
    } catch (err) {
      setError(err.userMessage || 'Erro ao rejeitar');
    } finally {
      setAprovando(false);
    }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="w-6 h-6 border-4 border-brand-primary border-t-transparent rounded-full animate-spin" /></div>;

  const inputCls = "w-full rounded-lg border border-brand-border px-3 py-2 text-sm font-body focus:outline-none focus:ring-2 focus:ring-brand-accent-mid focus:border-brand-primary bg-white";

  return (
    <div data-testid="cliente-form">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate(-1)} className="p-2 rounded-lg hover:bg-brand-bg"><ArrowLeft className="w-5 h-5 text-brand-text-muted" /></button>
        <h1 className="dashboard-heading text-xl">{isNew ? 'Novo Cliente' : (isPendente ? 'Aprovar Cadastro' : 'Editar Cliente')}</h1>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3 mb-4">{error}</div>}

      {isPendente && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
          <p className="font-dashboard font-semibold text-amber-800 text-sm mb-1">Cadastro Pendente de Aprovação</p>
          <p className="text-xs text-amber-700 font-body">Preencha os dados operacionais abaixo e clique em Aprovar.</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Dados cadastrais */}
        <div className="card-dashboard space-y-3">
          <h3 className="font-dashboard font-semibold text-brand-text">Dados Cadastrais</h3>
          <div>
            <label className="block text-xs text-brand-text-muted mb-1 font-body">Nome do Contato *</label>
            <input className={inputCls} value={form.nome_contato} onChange={e => set('nome_contato', e.target.value)} data-testid="field-nome-contato" />
          </div>
          <div>
            <label className="block text-xs text-brand-text-muted mb-1 font-body">Nome da Empresa</label>
            <input className={inputCls} value={form.nome_empresa} onChange={e => set('nome_empresa', e.target.value)} />
          </div>
          <div>
            <label className="block text-xs text-brand-text-muted mb-1 font-body">CPF / CNPJ</label>
            <input
              className={`${inputCls} ${cpfCnpjError ? 'border-red-400 focus:ring-red-300' : ''}`}
              value={form.cpf_cnpj}
              onChange={e => handleCpfCnpj(e.target.value)}
              placeholder="000.000.000-00 ou 00.000.000/0000-00"
              maxLength={18}
              data-testid="field-cpf-cnpj"
            />
            {cpfCnpjError && (
              <p className="text-xs text-red-600 mt-1 font-body" data-testid="cpf-cnpj-error">{cpfCnpjError}</p>
            )}
          </div>
          <div>
            <label className="block text-xs text-brand-text-muted mb-1 font-body">E-mail *</label>
            <input type="email" className={inputCls} value={form.email} onChange={e => set('email', e.target.value)} data-testid="field-email" />
          </div>
          {isAdmin && !isNew && (
            <div>
              <label className="block text-xs text-brand-text-muted mb-1 font-body">
                Nova Senha <span className="text-brand-text-muted/60">(deixe em branco para manter)</span>
              </label>
              <input
                type="password"
                className={inputCls}
                placeholder="Mínimo 6 caracteres"
                value={form.senha}
                onChange={e => set('senha', e.target.value)}
                data-testid="field-senha-cliente"
              />
            </div>
          )}
          {isNew && (
            <div>
              <label className="block text-xs text-brand-text-muted mb-1 font-body">Senha (para acesso ao app)</label>
              <input type="password" className={inputCls} value={form.senha} onChange={e => set('senha', e.target.value)} placeholder="Mín. 8 caracteres" />
            </div>
          )}
          <div>
            <label className="block text-xs text-brand-text-muted mb-1 font-body">WhatsApp</label>
            <input className={inputCls} value={form.whatsapp} onChange={e => set('whatsapp', maskPhone(e.target.value))} />
          </div>
        </div>

        {/* Endereço */}
        <div className="card-dashboard space-y-3">
          <h3 className="font-dashboard font-semibold text-brand-text">Endereço</h3>
          <div>
            <label className="block text-xs text-brand-text-muted mb-1 font-body">CEP</label>
            <CepInput value={form.cep} onChange={v => set('cep', v)} onAddressFound={handleCepFound} className={inputCls} />
          </div>
          <div>
            <label className="block text-xs text-brand-text-muted mb-1 font-body">Logradouro</label>
            <input className={inputCls} value={form.logradouro} onChange={e => set('logradouro', e.target.value)} />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-1">
              <label className="block text-xs text-brand-text-muted mb-1 font-body">Número</label>
              <input className={inputCls} value={form.numero} onChange={e => set('numero', e.target.value)} />
            </div>
            <div className="col-span-2">
              <label className="block text-xs text-brand-text-muted mb-1 font-body">Bairro</label>
              <input className={inputCls} value={form.bairro} onChange={e => set('bairro', e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2">
              <label className="block text-xs text-brand-text-muted mb-1 font-body">Cidade</label>
              <input className={inputCls} value={form.cidade} onChange={e => set('cidade', e.target.value)} />
            </div>
            <div>
              <label className="block text-xs text-brand-text-muted mb-1 font-body">UF</label>
              <input className={inputCls} value={form.uf} onChange={e => set('uf', e.target.value)} maxLength={2} />
            </div>
          </div>
          <div>
            <label className="block text-xs text-brand-text-muted mb-1 font-body">Ponto de Referência</label>
            <input className={inputCls} value={form.ponto_referencia} onChange={e => set('ponto_referencia', e.target.value)} />
          </div>
        </div>

        {/* Endereço de Entrega Diferente */}
        <div className="card-dashboard space-y-3 md:col-span-2">
          <div className="flex items-center justify-between">
            <h3 className="font-dashboard font-semibold text-brand-text">Endereço de Entrega</h3>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.endereco_entrega_diferente}
                onChange={e => set('endereco_entrega_diferente', e.target.checked)}
                className="rounded border-brand-border text-brand-primary"
                data-testid="toggle-endereco-entrega"
              />
              <span className="text-sm font-body text-brand-text">Endereço de entrega diferente do cadastro</span>
            </label>
          </div>
          {form.endereco_entrega_diferente && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2 border-t border-brand-border">
              <div>
                <label className="block text-xs text-brand-text-muted mb-1 font-body">CEP</label>
                <CepInput value={form.endereco_entrega?.cep || ''} onChange={v => setEntrega('cep', v)} onAddressFound={handleCepEntregaFound} className={inputCls} />
              </div>
              <div>
                <label className="block text-xs text-brand-text-muted mb-1 font-body">Logradouro</label>
                <input className={inputCls} value={form.endereco_entrega?.logradouro || ''} onChange={e => setEntrega('logradouro', e.target.value)} />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-1">
                  <label className="block text-xs text-brand-text-muted mb-1 font-body">Número</label>
                  <input className={inputCls} value={form.endereco_entrega?.numero || ''} onChange={e => setEntrega('numero', e.target.value)} />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs text-brand-text-muted mb-1 font-body">Bairro</label>
                  <input className={inputCls} value={form.endereco_entrega?.bairro || ''} onChange={e => setEntrega('bairro', e.target.value)} />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2">
                  <label className="block text-xs text-brand-text-muted mb-1 font-body">Cidade</label>
                  <input className={inputCls} value={form.endereco_entrega?.cidade || ''} onChange={e => setEntrega('cidade', e.target.value)} />
                </div>
                <div>
                  <label className="block text-xs text-brand-text-muted mb-1 font-body">UF</label>
                  <input className={inputCls} value={form.endereco_entrega?.uf || ''} onChange={e => setEntrega('uf', e.target.value)} maxLength={2} />
                </div>
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs text-brand-text-muted mb-1 font-body">Ponto de Referência</label>
                <input className={inputCls} value={form.endereco_entrega?.ponto_referencia || ''} onChange={e => setEntrega('ponto_referencia', e.target.value)} />
              </div>
            </div>
          )}
        </div>

        {/* Operacional - always visible for admin, relevant for operador too */}
        <div className="card-dashboard space-y-3">
          <h3 className="font-dashboard font-semibold text-brand-text">Configuração Operacional</h3>
          {isAdmin && (
            <div>
              <label className="block text-xs text-brand-text-muted mb-1 font-body">Tipo de Cliente *</label>
              <select className={inputCls} value={form.tipo_cliente_id} onChange={e => set('tipo_cliente_id', e.target.value)} data-testid="select-tipo-cliente">
                <option value="">Selecione...</option>
                {tiposCliente.map(t => <option key={t.id} value={t.id}>{t.nome}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="block text-xs text-brand-text-muted mb-1 font-body">Rota de Entrega *</label>
            <select className={inputCls} value={form.rota_id} onChange={e => set('rota_id', e.target.value)} data-testid="select-rota">
              <option value="">Selecione...</option>
              {rotas.map(r => <option key={r.id} value={r.id}>{r.nome}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-brand-text-muted mb-1 font-body">Turno *</label>
            <select className={inputCls} value={form.turno} onChange={e => set('turno', e.target.value)}>
              {TURNOS.map(t => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-brand-text-muted mb-1 font-body">Tipo de Entrega *</label>
            <select className={inputCls} value={form.tipo_entrega} onChange={e => set('tipo_entrega', e.target.value)}>
              {TIPOS_ENTREGA.map(t => <option key={t}>{t}</option>)}
            </select>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.prazo_24h} onChange={e => set('prazo_24h', e.target.checked)} className="rounded border-brand-border text-brand-primary" />
            <span className="text-sm font-body text-brand-text">Habilitar prazo 24h</span>
          </label>
          {!isPendente && (
            <div>
              <label className="block text-xs text-brand-text-muted mb-1 font-body">Status</label>
              <select className={inputCls} value={form.status} onChange={e => set('status', e.target.value)}>
                <option>Ativo</option>
                <option>Inativo</option>
                <option>Pendente</option>
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex gap-3 mt-6">
        <button onClick={() => navigate(-1)} className="px-5 py-2.5 rounded-lg border border-brand-border font-body text-sm">Cancelar</button>
        {isPendente ? (
          <>
            <div className="flex-1 flex gap-2">
              <input className="flex-1 rounded-lg border border-red-200 px-3 py-2 text-sm font-body" placeholder="Motivo da rejeição" value={motivoRejeicao} onChange={e => setMotivoRejeicao(e.target.value)} />
              <button onClick={handleRejeitar} disabled={aprovando} className="px-4 py-2.5 rounded-lg border-2 border-red-200 text-red-600 font-dashboard font-semibold text-sm disabled:opacity-60 flex items-center gap-2" data-testid="reject-btn">
                <X className="w-4 h-4" /> Rejeitar
              </button>
              <button onClick={handleAprovar} disabled={aprovando} className="px-5 py-2.5 rounded-lg btn-primary font-dashboard font-semibold text-sm disabled:opacity-60 flex items-center gap-2" data-testid="approve-btn">
                <Check className="w-4 h-4" /> {aprovando ? 'Aprovando...' : 'Aprovar'}
              </button>
            </div>
          </>
        ) : (
          <button onClick={handleSave} disabled={saving} className="px-6 py-2.5 rounded-lg btn-primary font-dashboard font-semibold text-sm disabled:opacity-60 flex items-center gap-2" data-testid="save-cliente-btn">
            <Save className="w-4 h-4" /> {saving ? 'Salvando...' : 'Salvar'}
          </button>
        )}
      </div>
    </div>
  );
}
